import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { readShadeThunk } from '@/stores/thunks/read-shade-thunk';
import { saveShadeThunk } from '@/stores/thunks/save-shade-thunk';
import { ShadeMode } from '@/stores/state';
import { Button } from './ui/button';
import { SunIcon } from './icons/sun-icon';
import { MoonIcon } from './icons/moon-icon';
import { setThemeMode } from '@/stores/theme-slice';

/**
 * Dark Mode Toggle Component
 * 
 * Toggles between light and dark mode using ShadCN theming
 * State is persisted in localStorage via Redux thunks
 */
export const DarkModeToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const mode = useAppSelector((state) => state.theme.mode);
  
  const isDarkMode = mode === ShadeMode.Dark;

  // Load mode from localStorage on component mount
  useEffect(() => {
    dispatch(readShadeThunk());
  }, [dispatch]);

  // Apply the theme to the document root
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDarkMode]);

  const handleToggle = () => {
    const newMode = isDarkMode ? ShadeMode.Light : ShadeMode.Dark;
    
    // Update the Redux state immediately for responsive UI
    dispatch(setThemeMode(newMode));
    
    // Save the new mode to localStorage
    dispatch(saveShadeThunk(newMode));
  };

  return (
    <Button
      variant="outline"
      size="icon"
      onClick={handleToggle}
      className="relative group"
      aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
    >
      <SunIcon className="h-[1.2rem] w-[1.2rem] scale-100 opacity-100 transition-all dark:scale-0 dark:opacity-0 group-hover:scale-125 group-active:scale-90" />
      <MoonIcon className="absolute h-[1.2rem] w-[1.2rem] scale-0 opacity-0 transition-all dark:scale-100 dark:opacity-100 dark:group-hover:scale-125 dark:group-active:scale-90" />
    </Button>
  );
};

export default DarkModeToggle;